/**
 * components/DailyLossMeter — the daily-loss governor as a bounded meter (dataviz
 * "magnitude vs a fixed reference" form). Today's realized P&L is measured against the
 * owner-fixed hard cap (the −2% daily-loss halt): the bar fills as the loss budget is
 * consumed and switches to a reserved status color as it approaches the cap.
 *
 * Status is never color-alone (dataviz rule): each state ships an icon + a text label.
 * A green/flat day shows an empty meter — there is no loss budget in use.
 */
import type { DailyLoss } from '../api/types';
import { pctPlain } from '../lib/format';

type Status = 'ok' | 'caution' | 'critical';

function statusOf(d: DailyLoss): Status {
  if (d.capHit || d.usedFraction >= 1) return 'critical';
  if (d.usedFraction >= 0.6) return 'caution';
  return 'ok';
}

const META: Record<Status, { icon: string; label: string }> = {
  ok: { icon: '●', label: 'Within budget' },
  caution: { icon: '▲', label: 'Approaching cap' },
  critical: { icon: '■', label: 'Cap hit — halted' },
};

interface DailyLossMeterProps {
  readonly daily: DailyLoss;
}

export function DailyLossMeter({ daily }: DailyLossMeterProps): JSX.Element {
  const status = statusOf(daily);
  const meta = META[status];
  // The meter tracks the loss budget: 0% on a flat/green day, 100% at the cap.
  const fill = Math.max(0, Math.min(1, daily.usedFraction));
  const capLabel = `−${pctPlain(daily.capPct, 1)}`;

  return (
    <div className={`meter meter--${status}`}>
      <div className="meter__head">
        <span className={`meter__status meter__status--${status}`}>
          <span aria-hidden className="meter__icon">{meta.icon}</span>
          {meta.label}
        </span>
        <span className="meter__trades muted">
          {daily.tradesToday} {daily.tradesToday === 1 ? 'trade' : 'trades'} today
        </span>
      </div>

      <div
        className="meter__track"
        role="meter"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(fill * 100)}
        aria-label={`Daily loss budget used, ${Math.round(fill * 100)} percent of the ${capLabel} cap`}
      >
        <div className="meter__fill" style={{ width: `${fill * 100}%` }} />
        {/* cap line sits at 100% — the point at which trading auto-halts */}
        <span className="meter__cap" aria-hidden />
      </div>

      <div className="meter__foot">
        <span>
          <span className="meter__foot-label muted">Realized today</span>{' '}
          <span className={`num ${daily.realizedPct < 0 ? 'loss' : daily.realizedPct > 0 ? 'profit' : ''}`}>
            {daily.realizedPct > 0 ? '+' : daily.realizedPct < 0 ? '−' : ''}
            {pctPlain(Math.abs(daily.realizedPct), 2)}
          </span>
        </span>
        <span>
          <span className="meter__foot-label muted">Loss cap</span>{' '}
          <span className="num">{capLabel}</span>
        </span>
      </div>
    </div>
  );
}
